import { ActiveTab, BreakReasonItem, ReconTradeItem } from "../../../Types/Reconautomation.types";

export type ExportTab = ActiveTab;
export type ExportSide = "acers" | "bgm";

interface ExportColumn<T> {
  header: string;
  accessor: (row: T) => string | number | null | undefined;
}

const escapeCell = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined) return "";
  const str = String(value);
  if (str.includes(",") || str.includes('"') || str.includes("\n")) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

const buildCSV = <T>(columns: ExportColumn<T>[], rows: T[]): string => {
  const header = columns.map((c) => escapeCell(c.header)).join(",");
  const body = rows
    .map((row) => columns.map((c) => escapeCell(c.accessor(row))).join(","))
    .join("\n");
  return `${header}\n${body}`;
};

const triggerDownload = (csvContent: string, filename: string): void => {
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const formatBreakReasonItems = (items?: BreakReasonItem[] | null): string => {
  if (!items || !items.length) return "";
  return items
    .map((item) => {
      const parts: string[] = [item.reason];

      if (item.bgm_price != null || item.acers_price != null) {
        parts.push(`BGM Price: ${item.bgm_price ?? "—"}`, `ACERS Price: ${item.acers_price ?? "—"}`);
      }
      if (item.bgm_quantity != null || item.acers_quantity != null) {
        parts.push(`BGM Qty: ${item.bgm_quantity ?? "—"}`, `ACERS Qty: ${item.acers_quantity ?? "—"}`);
      }
      return parts.join(" | ");
    })
    .join("; ");
};

const rowBreakReason = (row: ReconTradeItem): string => formatBreakReasonItems(row.reasons);

const rowCanonStatus = (row: ReconTradeItem): string => {
  if (!row.canonicalisation_status || !row.canonicalisation_status.length) return "";
  return row.canonicalisation_status.join("; ");
};

const rowTradeIds = (row: ReconTradeItem): string =>
  (row.trade_ids ?? []).filter(Boolean).join("; ");

const BASE_COLUMNS: ExportColumn<ReconTradeItem>[] = [
  { header: "Trade ID", accessor: (r) => rowTradeIds(r) },
  { header: "Trade Date", accessor: (r) => r.trade_date },
  { header: "Client", accessor: (r) => r.client },
  { header: "Contract", accessor: (r) => r.contract },
  { header: "Contract Month", accessor: (r) => r.contract_month },
  { header: "Direction", accessor: (r) => r.direction },
  { header: "Canonical Qty", accessor: (r) => r.quantity },
  { header: "Price", accessor: (r) => r.price },
];

const ALL_COLUMNS: ExportColumn<ReconTradeItem>[] = [
  ...BASE_COLUMNS,
  { header: "Break Reason", accessor: (r) => rowBreakReason(r) },
  { header: "Canonical Status", accessor: (r) => rowCanonStatus(r) },
  { header: "Status", accessor: (r) => r.status },
];

const MATCHED_COLUMNS: ExportColumn<ReconTradeItem>[] = [
  ...BASE_COLUMNS,
  { header: "Canonical Status", accessor: (r) => rowCanonStatus(r) },
  { header: "Status", accessor: (r) => r.status },
];

const MISMATCHES_COLUMNS: ExportColumn<ReconTradeItem>[] = [
  ...BASE_COLUMNS,
  { header: "Break Reason", accessor: (r) => rowBreakReason(r) },
  { header: "Canonical Status", accessor: (r) => rowCanonStatus(r) },
  { header: "Status", accessor: (r) => r.status },
];

const UNMATCHED_COLUMNS: ExportColumn<ReconTradeItem>[] = [
  ...BASE_COLUMNS,
  { header: "Break Reason", accessor: (r) => rowBreakReason(r) },
  { header: "Canonical Status", accessor: (r) => rowCanonStatus(r) },
  { header: "Status", accessor: (r) => r.status },
];

const FAILURES_COLUMNS: ExportColumn<ReconTradeItem>[] = [
  { header: "Trade ID", accessor: (r) => rowTradeIds(r) },
  { header: "Trade Date", accessor: (r) => r.trade_date },
  { header: "Client", accessor: (r) => r.client },
  { header: "Contract", accessor: (r) => r.contract },
  { header: "Contract Month", accessor: (r) => r.contract_month },
  { header: "Direction", accessor: (r) => r.direction },
  { header: "Canonical Status", accessor: (r) => rowCanonStatus(r) },
  { header: "Status", accessor: (r) => r.status },
  { header: "Break Reason", accessor: (r) => rowBreakReason(r) },
];

const COLUMNS_BY_TAB: Record<ExportTab, ExportColumn<ReconTradeItem>[]> = {
  all: ALL_COLUMNS,
  matched: MATCHED_COLUMNS,
  mismatches: MISMATCHES_COLUMNS,
  unmatched: UNMATCHED_COLUMNS,
  failures: FAILURES_COLUMNS,
  sourceIssue: FAILURES_COLUMNS,
};

const FILENAME_BY_TAB: Record<ExportTab, string> = {
  all: "recon_all",
  matched: "recon_matched",
  mismatches: "recon_mismatches",
  unmatched: "recon_unmatched",
  failures: "recon_failures",
  sourceIssue: "recon_source_issue",
};

export const exportReconCSV = (
  tab: ExportTab,
  side: ExportSide,
  rows: ReconTradeItem[],
  runId?: string | null
): void => {
  if (!rows.length) return;

  const columns = COLUMNS_BY_TAB[tab];
  const csv = buildCSV(columns, rows);

  const timestamp = new Date().toISOString().slice(0, 10);
  const suffix = runId ? `_${runId}` : "";
  const filename = `${FILENAME_BY_TAB[tab]}_${side}${suffix}_${timestamp}.csv`;

  triggerDownload(csv, filename);
};